import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * useDebouncedAsync
 *
 * A React hook that provides a debounced version of an asynchronous function with built-in
 * cancellation using AbortController. Each new call cancels the previous one if it's still pending.
 *
 * Usage:
 * const { debouncedFunction, isPending, error, result } = useDebouncedAsync(fetchData, 500, { rethrow: true });
 *
 * Notes:
 * - Automatically cleans up pending requests on component unmount.
 * - Ensures only the latest async call is executed after the debounce period.
 */

interface UseDebouncedAsyncOptions {
  rethrow?: boolean;
}

type AsyncFunctionType<FunctionArgs extends [...unknown[]], FunctionResult> = (
  ...args: [...FunctionArgs, AbortSignal]
) => Promise<FunctionResult>;

interface UseDebouncedAsyncReturn<Args extends unknown[], Result> {
  debouncedFunction: (...args: Args) => Promise<Result | undefined>;
  isPending: boolean;
  error: unknown;
  result: Result | null;
}

export function useDebouncedAsync<
  FunctionArgs extends [...unknown[]],
  FunctionResult,
>(
  asyncFunction: AsyncFunctionType<FunctionArgs, FunctionResult>,
  delay: number,
  options?: UseDebouncedAsyncOptions,
): UseDebouncedAsyncReturn<FunctionArgs, FunctionResult> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<FunctionResult | null>(null);

  const debouncedFunction = useCallback(
    (...args: FunctionArgs): Promise<FunctionResult> => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      return new Promise<FunctionResult>((resolve, reject) => {
        timeoutRef.current = setTimeout(async () => {
          setIsPending(true);
          setError(null);
          setResult(null);

          try {
            const res = await asyncFunction(...args, controller.signal);
            if (!controller.signal.aborted) {
              setResult(res);
              resolve(res);
            }
          } catch (err: any) {
            if (err.name !== 'AbortError') {
              setError(err);
              if (options?.rethrow) {
                reject(err);
              }
            }
          } finally {
            setIsPending(false);
          }
        }, delay);
      });
    },
    [asyncFunction, delay, options],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return { debouncedFunction, isPending, error, result };
}
